import AddIcon from "@mui/icons-material/Add";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import StopIcon from "@mui/icons-material/Stop";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { ICampanha, ICampanhaLog, StatusCampanha } from "../../interfaces/ICampanha";
import { useCampanhas } from "../../hooks/useCampanhas";
import { useFetchCampanhas } from "../../hooks/useFetchCampanhas";
import { CampanhaTableRow } from "../../components/CampanhaTableRow";
import { CampanhaFormDrawer } from "../../components/CampanhaFormDrawer";
import FullScreenLoading from "../../../core/components/FullScreenLoading";
import { formatarDataHumana } from "../../utils/campanha";
import { FiltrosContainer, Loading, PageContainer, TableContainer } from "./styles";

const statusOptions: StatusCampanha[] = ["RASCUNHO", "AGENDADA", "ATIVA", "PAUSADA", "FINALIZADA", "CANCELADA", "FALHA"];

export const ListarCampanhas = () => {
    const snackbar = useSnackbar();
    const [openForm, setOpenForm] = useState(false);
    const [campanhaEdicao, setCampanhaEdicao] = useState<ICampanha | null>(null);
    const [logs, setLogs] = useState<ICampanhaLog[]>([]);
    const [openLogs, setOpenLogs] = useState(false);
    const [campanhaSelecionada, setCampanhaSelecionada] = useState<ICampanha | null>(null);
    const [campanhaResumo, setCampanhaResumo] = useState<ICampanha | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const openAcoes = Boolean(anchorEl);

    const {
        criar,
        atualizar,
        listarLogs,
        abrir,
        pausar,
        retomar,
        finalizar,
        cancelar,
        loading
    } = useCampanhas();

    const {
        dados,
        isLoading,
        handleRefresh,
        handleLimpar,
        handleFiltrar,
        nome,
        setNome,
        status,
        setStatus,
        inicioAs,
        setInicioAs,
        terminoAs,
        setTerminoAs
    } = useFetchCampanhas();

    const campanhas: ICampanha[] = Array.isArray(dados) ? dados : [];

    const statusSelecionado = campanhaSelecionada?.status ?? "RASCUNHO";

    const abrirFormulario = (campanha?: ICampanha) => {
        setCampanhaEdicao(campanha ?? null);
        setOpenForm(true);
    };

    const fecharFormulario = () => {
        setOpenForm(false);
        setCampanhaEdicao(null);
    };

    const salvarCampanha = async (payload: ICampanha) => {
        const resultado = campanhaEdicao?.uuid
            ? await atualizar(campanhaEdicao.uuid, payload)
            : await criar(payload);

        if(resultado) {
            snackbar("Campanha salva com sucesso", { severity: "success" });
            fecharFormulario();
            handleRefresh();
        }
    };

    const executarAcao = async (acao: "abrir" | "pausar" | "retomar" | "finalizar" | "cancelar") => {
        if(!campanhaSelecionada?.uuid) return;
        fecharAcoes();
        setLoadingAcao(true);
        try {
            const mapa = { abrir, pausar, retomar, finalizar, cancelar };
            const resultado = await mapa[acao](campanhaSelecionada.uuid);
            if(resultado) {
                snackbar("Campanha atualizada com sucesso", { severity: "success" });
                setCampanhaSelecionada(null);
                handleRefresh();
            }
        } finally {
            setLoadingAcao(false);
        }
    };

    const abrirLogs = async () => {
        if(!campanhaSelecionada?.uuid) return;
        fecharAcoes();
        const dadosLogs = await listarLogs(campanhaSelecionada.uuid);
        if(dadosLogs) {
            setLogs(dadosLogs);
            setOpenLogs(true);
        }
    };

    const handleEditar = () => {
        if(!campanhaSelecionada) return;
        fecharAcoes();
        abrirFormulario(campanhaSelecionada);
    };

    const abrirAcoes = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    const toggleCampanhaSelecionada = (campanha: ICampanha) => {
        setCampanhaSelecionada(prev => (prev?.uuid === campanha.uuid ? null : campanha));
    };

    return (
        <PageContainer>
            {loadingAcao && <FullScreenLoading />}

            <FiltrosContainer>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>Campanhas</Typography>
                    <TextField size="small" label="Nome" value={nome} onChange={(event) => setNome(event.target.value)} />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value as StatusCampanha | "")}>
                            <MenuItem value="">Todos</MenuItem>
                            {statusOptions.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField size="small" label="Início" type="datetime-local" value={inicioAs} onChange={(event) => setInicioAs(event.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="Término" type="datetime-local" value={terminoAs} onChange={(event) => setTerminoAs(event.target.value)} InputLabelProps={{ shrink: true }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: 3 }}>
                    <Button variant="outlined" onClick={handleLimpar}>Limpar</Button>
                    <Button variant="contained" onClick={handleFiltrar}>Filtrar</Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <Button
                        disabled={!campanhaSelecionada}
                        variant="contained"
                        endIcon={
                            <ArrowDropDown sx={{ transform: openAcoes ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
                        }
                        onClick={abrirAcoes}
                    >
                        Ações
                    </Button>
                    <Menu anchorEl={anchorEl} open={openAcoes} onClose={fecharAcoes}>
                        <MenuItem disabled>
                            <Typography variant="inherit" noWrap>
                                {campanhaSelecionada?.nome ?? ""}
                            </Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleEditar} disabled={!["RASCUNHO", "AGENDADA", "PAUSADA", "FINALIZADA"].includes(statusSelecionado)}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Editar</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => executarAcao("abrir")} disabled={!["RASCUNHO", "AGENDADA", "FINALIZADA"].includes(statusSelecionado)}>
                            <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Abrir</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => executarAcao("pausar")} disabled={statusSelecionado !== "ATIVA"}>
                            <ListItemIcon><PauseIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Pausar</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => executarAcao("retomar")} disabled={statusSelecionado !== "PAUSADA"}>
                            <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Retomar</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => executarAcao("finalizar")} disabled={!["ATIVA", "PAUSADA"].includes(statusSelecionado)}>
                            <ListItemIcon><StopIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Finalizar</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => executarAcao("cancelar")} disabled={!["RASCUNHO", "AGENDADA"].includes(statusSelecionado)}>
                            <ListItemIcon><CancelIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Cancelar</Typography>
                        </MenuItem>
                        <MenuItem onClick={abrirLogs}>
                            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                            <Typography variant="inherit">Ver Logs</Typography>
                        </MenuItem>
                    </Menu>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirFormulario()}>Nova</Button>
                </Box>
            </FiltrosContainer>

            <Paper sx={{ width: "70%", mx: "auto" }}>
                <TableContainer>
                    {isLoading ? (
                        <Loading><CircularProgress /></Loading>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Início</TableCell>
                                    <TableCell>Término</TableCell>
                                    <TableCell>Itens</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {campanhas.map(campanha => (
                                    <CampanhaTableRow
                                        key={campanha.uuid}
                                        campanha={campanha}
                                        selecionada={campanhaSelecionada?.uuid === campanha.uuid}
                                        onToggleSelecionada={toggleCampanhaSelecionada}
                                        onVerResumo={setCampanhaResumo}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Paper>

            <CampanhaFormDrawer
                open={openForm}
                campanha={campanhaEdicao}
                onClose={fecharFormulario}
                onSalvar={salvarCampanha}
                loading={loading}
            />

            {/* Drawer de resumo da campanha */}
            <Drawer anchor="right" open={Boolean(campanhaResumo)} onClose={() => setCampanhaResumo(null)}
                PaperProps={{ sx: { width: { xs: "100vw", sm: 480 } } }}>
                {campanhaResumo && (
                    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: 1, borderColor: "divider" }}>
                            <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>{campanhaResumo.nome}</Typography>
                            <IconButton onClick={() => setCampanhaResumo(null)}><CloseIcon /></IconButton>
                        </Box>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Chip label={campanhaResumo.status ?? "RASCUNHO"} size="small" color={
                                campanhaResumo.status === "ATIVA" ? "success" :
                                campanhaResumo.status === "PAUSADA" ? "warning" :
                                campanhaResumo.status === "AGENDADA" ? "primary" :
                                ["CANCELADA", "FALHA"].includes(campanhaResumo.status ?? "") ? "error" : "default"
                            } />
                            {campanhaResumo.descricao && (
                                <Typography variant="body2" color="text.secondary" sx={{ width: "100%" }}>{campanhaResumo.descricao}</Typography>
                            )}
                            {campanhaResumo.inicioAs && (
                                <Typography variant="caption" color="text.secondary">Início: {formatarDataHumana(campanhaResumo.inicioAs)}</Typography>
                            )}
                            {campanhaResumo.terminoAs && (
                                <Typography variant="caption" color="text.secondary">Término: {formatarDataHumana(campanhaResumo.terminoAs)}</Typography>
                            )}
                        </Box>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle2">{campanhaResumo.itens?.length ?? 0} iten(s) na campanha</Typography>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            <List disablePadding>
                                {(campanhaResumo.itens ?? []).map((item, i) => (
                                    <ListItem key={i} divider>
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap title={item.caminho}>{item.caminho}</Typography>}
                                            secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                    De R$ {Number(item.precoOriginal).toFixed(2).replace(".", ",")} por R$ {Number(item.precoPromocional).toFixed(2).replace(".", ",")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>
                )}
            </Drawer>

            <Dialog open={openLogs} onClose={() => setOpenLogs(false)} fullWidth maxWidth="md">
                <DialogTitle>Logs da campanha</DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ação</TableCell>
                                <TableCell>Origem</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Executado em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map(log => (
                                <TableRow key={log.uuid}>
                                    <TableCell>{log.acao}</TableCell>
                                    <TableCell>{log.origem}</TableCell>
                                    <TableCell>{log.statusAnterior ?? "-"} → {log.statusNovo ?? "-"}</TableCell>
                                    <TableCell>{formatarDataHumana(log.executadoAs)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLogs(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};
